import { useTranslation } from 'react-i18next';
import { FreelancerSearchResult } from '../../types/search';
import FreelancerCard from './FreelancerCard';
import { useMemo, useState } from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import FreelancerProfileModal from './FreelancerProfileModal';
import { ActiveAlliance } from '../../types/alliance';
import { mapActiveAllianceToSearchResult } from '../../utils/freelancerUtils';
import { FreelancerProfile } from '../../types/profile';
import CollapsibleContent from '../common/CollapsibleContent';
import { useModal } from '../../context/ModalContext';

interface ProfileAlliancesSectionProps {
  alliances?: ActiveAlliance[];
  className?: string;
  profile: FreelancerProfile;
  hasMoreAlliances: boolean;
  loading: boolean;
  fetchMoreAlliances: () => void;
}

const ProfileAlliancesSection = ({
  alliances = [],
  className,
  loading,
}: ProfileAlliancesSectionProps) => {
  const { t } = useTranslation(['profile', 'common']);
  const [selectedFreelancer, setSelectedFreelancer] = useState<FreelancerSearchResult | null>(null);

  const alliancesAsSearchResults = useMemo(
    () => alliances.map(alliance => mapActiveAllianceToSearchResult(alliance)),
    [alliances]
  );

  const { setIsAnyModalOpen } = useModal();
  const numberOfAlliances = alliancesAsSearchResults.length;
  return (
    <div
      className={`bg-white  rounded-2xl mb-6  border-2 shadow-xl border-gray-300  py-4 px-6 flex flex-col ${className}`}
    >
      <CollapsibleContent
        header={
          <div className="mb-2">
            <h3 className="relative inline-block text-lg font-bold text-secondary">
              {t('alliances')}

              <span className="absolute -right-3 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-warning text-[9px] font-semibold text-lightest">
                {numberOfAlliances}
              </span>
            </h3>
          </div>
        }
      >
        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {alliancesAsSearchResults.length > 0 ? (
              alliancesAsSearchResults.map(alliance => {
                const isLast =
                  alliance === alliancesAsSearchResults[alliancesAsSearchResults.length - 1];
                return (
                  <FreelancerCard
                    key={alliance.id}
                    freelancer={alliance}
                    complete={false}
                    className={`px-0 py-3 ${isLast ? '' : 'border-b border-[#E0E0E0]'}`}
                    onClick={() => {
                      setSelectedFreelancer(alliance);
                      setIsAnyModalOpen(false);
                    }}
                  />
                );
              })
            ) : (
              <div className="mt-8 text-center text-sm text-secondary">{t('noAlliances')}</div>
            )}
          </div>
        )}
        {/* Popup modal for freelancer details */}
        {selectedFreelancer && (
          <FreelancerProfileModal
            freelancer={selectedFreelancer}
            onClose={() => {
              setSelectedFreelancer(null);
              setIsAnyModalOpen(false);
            }}
          />
        )}
      </CollapsibleContent>
    </div>
  );
};

export default ProfileAlliancesSection;
